import React, { useEffect, useState } from 'react';
import Button from '../Button';
import { MenuCategoriesProps, MenuData } from './types';
import {
  ButtonText,
  FullWidthDivider,
  GroupHeader,
  GroupHeaderText,
  GroupItems,
} from './styles';
import { Menu } from '@mui/material';
import MenuItemAll from './MenuItemAll';

// Native JavaScript replacement for lodash groupBy and mapValues
const groupByCategory = (
  items: MenuData[],
): Record<string, Omit<MenuData, 'category'>[]> => {
  const grouped: Record<string, Omit<MenuData, 'category'>[]> = {};

  for (const item of items) {
    const category = item.category;
    if (!grouped[category]) {
      grouped[category] = [];
    }
    // Omit category property from the item
    const { category: _, ...itemWithoutCategory } = item;
    grouped[category].push(itemWithoutCategory);
  }

  return grouped;
};

const CategoricMenu: React.FC<MenuCategoriesProps> = ({
  'aria-label': ariaLabel,
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  buttonText,
  buttonVariant = 'contained',
  buttonSx,
  'data-testid': dataTestId,
  handleNavigationClick,
  options,
  sort,
  sortOrder,
  style,
  sx,
  transformOrigin = { vertical: 'top', horizontal: 'left' },
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const grouped = groupByCategory(options);

  const order = Object.keys(grouped);

  if (sort && !sortOrder) {
    order.sort();
  }

  const groupedArray = Object.entries(grouped).map(([category, items]) => ({
    category,
    items,
  }));

  // Native JavaScript replacement for lodash sortBy
  const groupedSortedArray = sortOrder
    ? groupedArray.sort((a, b) => {
        const indexA = sortOrder.indexOf(a.category);
        const indexB = sortOrder.indexOf(b.category);
        return indexA - indexB;
      })
    : sort
    ? groupedArray.sort((a, b) => a.category.localeCompare(b.category))
    : groupedArray;
  let menuItems: NodeListOf<HTMLElement> | null = null;

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          menuItems = document.querySelectorAll('[role="menuitem"]');
          menuItems[0]?.focus();
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    return () => {
      observer.disconnect();
    };
  }, [Boolean(anchorEl)]);

  let currentIndex = 0;
  const handleKeyboardNavigation = (e: React.KeyboardEvent) => {
    if (anchorEl && menuItems && menuItems.length > 0) {
      let newFocus: HTMLElement | null = null;
      switch (e.key) {
        case 'ArrowUp':
          if (currentIndex === 0) {
            currentIndex = menuItems.length;
          }
          currentIndex--;
          newFocus = menuItems[currentIndex] as HTMLElement;
          break;
        case 'ArrowDown':
          if (currentIndex === menuItems.length - 1) {
            currentIndex = -1;
          }
          currentIndex++;
          newFocus = menuItems[currentIndex] as HTMLElement;
          break;
      }
      if (newFocus) {
        newFocus.focus();
      }
    }
  };

  return (
    <div data-testid={dataTestId}>
      <Button
        aria-expanded={!!anchorEl}
        aria-haspopup="true"
        aria-label={ariaLabel}
        data-testid={`${dataTestId}-button`}
        disableFocusRipple
        disableRipple
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={buttonSx}
        variant={buttonVariant}
      >
        <ButtonText>{buttonText}</ButtonText>
      </Button>
      <Menu
        aria-label={ariaLabel}
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        onKeyDown={handleKeyboardNavigation}
        onClose={() => {
          setAnchorEl(null);
        }}
        open={Boolean(anchorEl)}
        slotProps={{
          paper: {
            sx,
            style,
          },
        }}
        data-testid={`${dataTestId}-menu`}
        transformOrigin={transformOrigin}
      >
        {groupedSortedArray.map((value, index) => (
          <li
            role="group"
            aria-labelledby={`${value.category}`}
            key={value.category}
          >
            <GroupHeader role="presentation" id={`${value.category}`}>
              <GroupHeaderText>{value.category}</GroupHeaderText>
            </GroupHeader>
            <GroupItems
              aria-label={`${value.category}`}
              key={`${value.category}-groupitems`}
              role="menu"
            >
              {value.items.map((item) => (
                <MenuItemAll
                  aria-label={ariaLabel}
                  data-testid={`${dataTestId}-${item.primary}`}
                  menuItem={item}
                  handleNavigation={handleNavigationClick}
                  key={`${item.primary} ${item.secondary}`}
                />
              ))}
            </GroupItems>
            {index !== groupedSortedArray.length - 1 && (
              <FullWidthDivider role="separator" aria-hidden={true} />
            )}
          </li>
        ))}
      </Menu>
    </div>
  );
};

export default CategoricMenu;
